import { AlertCircle, AlertTriangle, CheckCircle, Search, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { FormInput, FormSelect, FormTextArea } from '../forms';
import { FormRadioGroup } from '../forms/FormRadioGroup';

const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '12:00',
  '14:00', '15:00', '16:00'
];

const ClientSearch = ({ onSelect, onClose }) => {
  const { get } = useApi();
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const searchClients = async () => {
      if (query.length < 2) return;
      try {
        setLoading(true);
        const response = await get(`/users/search?query=${query}&role=student`);
        setClients(response.data.data || []);
      } catch (error) {
        console.error('Failed to search clients:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchClients, 300);
    return () => clearTimeout(debounce);
  }, [query, get]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md shadow-2xl border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Search Client</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            ×
          </button>
        </div>
        <div className="p-6">
          <div className="relative mb-4">
            <input
              type="text"
              className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white transition-all"
              placeholder="Search by name or ID..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Search className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
          </div>

          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Searching...</p>
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400">No clients found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {clients.map(client => (
                  <button
                    key={client._id}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 rounded-xl border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-200 group"
                    onClick={() => onSelect(client)}
                  >
                    <div className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{client.fullName}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{client.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const BookAppointment = ({ onClose, isUrgent: initialUrgent = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, post } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [counselors, setCounselors] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isUrgent, setIsUrgent] = useState(initialUrgent);

  const [formData, setFormData] = useState({
    counselorId: '',
    sessionType: '',
    serviceType: '',
    studentType: '',
    date: '',
    timeSlot: '',
    clientId: ['student', 'staff'].includes(user.role) ? user.id : '',
    urgencyReason: '',
    notes: ''
  });

  const [blockedDates, setBlockedDates] = useState([])

  const isDateBlocked = (date) => {
    const dayEvents = blockedDates.find(blockedDate => blockedDate.date === date);
    return dayEvents && dayEvents.events.length > 0;
  };

  const isTimeSlotBlocked = (date, timeSlot) => {
    // Assume blockedDates is an array with items like:
    // { date: "2025-02-12", events: [{ startTime: "10:00", endTime: "11:00", type: "event" }] }
    const dayEvents = blockedDates.find(item => item.date === date);
    if (!dayEvents) return false;
    return dayEvents.events.some(event =>
      event.type === 'event' && timeSlot >= event.startTime && timeSlot < event.endTime
    );
  };



  useEffect(() => {
    const fetchBlockedSlots = async () => {
      try {
        // Fetch individual blocked time slots
        const responseSlots = await get('/appointments/blocked-slots');
        // Fetch recurring blocked slots
        const responseRecurring = await get('/recurring-blocks');
  
        if (responseSlots.data?.status === 'success' && responseRecurring.data?.status === 'success') {
          // Format the blocked slots data
          const formattedSlots = responseSlots.data.data.map(item => ({
            date: item.date.split('T')[0], 
            events: [{ startTime: item.startTime, endTime: item.endTime, type: item.type }]
          }));
  
          // Format the recurring blocks data
          const formattedRecurring = responseRecurring.data.data.map(item => ({
            recurrenceRule: item.recurrenceRule, // Example: "Every Monday 9AM-12PM"
            events: [{ startTime: item.startTime, endTime: item.endTime, type: item.type }]
          }));
  
          // Update state with both regular and recurring blocks
          setBlockedDates([...formattedSlots, ...formattedRecurring]);
        }
      } catch (err) {
        console.error('Failed to fetch blocked slots:', err);
      }
    };
  
    fetchBlockedSlots();
  }, [get]); // Dependency array ensures it runs when `get` changes
  

  useEffect(() => {
    const fetchCounselors = async () => {
      try {
        const response = await get('/users/counselors');
        if (response.data?.status === 'success') {
          setCounselors(response.data.data);
        }
      } catch (err) {
        setError('Failed to load counselors');
      }
    };

    fetchCounselors();
  }, [get]);

  useEffect(() => {
    const fetchAssignedCounselor = async () => {
      if (user && ['student', 'staff'].includes(user.role) && user.id) {
        try {
          const response = await get(`/users/${user.id}/assigned-counselor`);
          if (response.data?.status === 'success' && response.data.data) {
            // Existing client with assigned counselor
            setFormData(prev => ({
              ...prev,
              counselorId: response.data.data._id
            }));
          } else {
            // New client with no assigned counselor
            setFormData(prev => ({
              ...prev,
              counselorId: '' // Clear this to allow selection
            }));
          }
        } catch (error) {
          console.error('Failed to fetch assigned counselor:', error);
          // Set counselorId to empty to enable dropdown selection
          setFormData(prev => ({
            ...prev,
            counselorId: ''
          }));
        }
      }
    };

    if (user && user.id) {
      fetchAssignedCounselor();
    }
  }, [user, get]);

  useEffect(() => {
    const fetchAvailableSlots = async () => {
      if (!formData.counselorId || !formData.date) return;

      try {
        setLoading(true);
        const response = await get('/appointments/available-slots', {
          params: { counselorId: formData.counselorId, date: formData.date }
        });

        if (response.data?.status === 'success') {
          const fetchedSlots = response.data.data.slots || TIME_SLOTS;
          const filteredSlots = fetchedSlots.filter(slot => !isTimeSlotBlocked(formData.date, slot));

          // Handle blocked reason if it exists
          if (response.data.data.blockedReason) {
            setError(response.data.data.blockedReason);
            setAvailableSlots([]);
          } else if (filteredSlots.length === 0) {
            setError('No available time slots for this date. Please choose another.');
            setAvailableSlots([]);
          } else {
            setError(''); // Clear any previous error messages
            setAvailableSlots(filteredSlots);
          }
        }
      } catch (err) {
        console.error('Error fetching available slots:', err);
        // More specific error handling based on the error type
        if (err.response?.status === 400) {
          setError(err.response.data.message || 'Invalid request. Please try again.');
        } else if (err.response?.status === 404) {
          setError('No schedule found for this date.');
        } else {
          setError('This date is reserved as a holiday. Please select another date');
        }
        setAvailableSlots([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableSlots();
  }, [formData.counselorId, formData.date, get]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const endpoint = isUrgent ? '/appointments/urgent' : '/appointments';
      const response = await post(endpoint, {
        ...formData,
        clientId: ['student', 'staff'].includes(user.role) ? user.id : selectedClient?._id
      });

      if (response.data?.status === 'success') {
        setSuccess(true);
        // Delay navigation to show success message
        setTimeout(() => {
          const basePath = `/${user.role}`;
          if (onClose) {
            onClose();
            navigate(`${basePath}/appointments`);
          } else {
            navigate(`${basePath}/appointments/${response.data.data._id}`);
          }
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, clientId: client._id }));
    setShowClientSearch(false);
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(`/${user.role}/appointments`);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto bg-white dark:bg-slate-800 rounded-lg shadow p-6">
        <div className="text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-medium text-gray-900 dark:text-white mb-2">
            Appointment Booked Successfully
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Redirecting you to the appointment details...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full dark:bg-slate-800">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50 sticky top-0 z-10 backdrop-blur-sm">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
            {isUrgent ? 'Book Urgent Appointment' : 'Book Appointment'}
          </h2>
          {user.role === 'receptionist' && (
            <label className="flex items-center px-3 py-1 bg-red-50 dark:bg-red-900/20 rounded-full border border-red-200 dark:border-red-800 cursor-pointer transition-colors hover:bg-red-100 dark:hover:bg-red-900/30">
              <input
                type="checkbox"
                checked={isUrgent}
                onChange={(e) => setIsUrgent(e.target.checked)}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm font-medium text-red-600 dark:text-red-400">Urgent Case</span>
            </label>
          )}
        </div>
        <button
          onClick={handleCancel}
          className="p-2 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        >
          ×
        </button>
      </div>

      {error && (
        <div className="px-6 pt-6">
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 rounded-r-lg flex items-center animate-fade-in shadow-sm">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="ml-3 text-sm text-red-700 dark:text-red-300 font-medium whitespace-pre-line">{error}</p>
          </div>
        </div>
      )}

      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {user.role === 'receptionist' && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client</label>
                    <button
                      type="button"
                      onClick={() => setShowClientSearch(true)}
                      className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 transition-colors"
                    >
                      Search Client
                    </button>
                  </div>
                  {selectedClient ? (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800 transition-all">
                      <div className="font-medium text-gray-900 dark:text-white">{selectedClient.fullName}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{selectedClient.email}</div>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 dark:bg-slate-900 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 text-sm flex items-center justify-center h-24 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors cursor-pointer" onClick={() => setShowClientSearch(true)}>
                      No client selected
                    </div>
                  )}
                </div>
              )}
              <FormRadioGroup
                label="Student Type"
                name="studentType"
                options={[
                  { value: 'online', label: 'Online' },
                  { value: 'oncampus', label: 'On Campus' },
                ]}
                value={formData.studentType}
                onChange={(e) => setFormData(prev => ({ ...prev, studentType: e.target.value }))}
                inline
                required
                className="pb-2 pt-1.5"
              />
              <FormSelect
                label="Session Type"
                name="sessionType"
                value={formData.sessionType}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  sessionType: e.target.value
                }))}
                options={[
                  { value: 'physical', label: 'Physical' },
                  { value: 'virtual', label: 'Virtual' },
                ]}
                required
              />
              <FormSelect
                label="Service Type"
                name="serviceType"
                value={formData.serviceType}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  serviceType: e.target.value
                }))}
                options={[
                  { value: 'personal-counseling', label: 'Personal Counseling' },
                  // { value: 'vct', label: 'VCT' },
                  // { value: 'consultation', label: 'Consultation' },
                  { value: 'couple-therapy', label: 'Couple Therapy' },
                  { value: 'family-counseling', label: 'Family Counseling' },
                  { value: 'group-therapy', label: 'Group Therapy' }
                ]}
                required
              />
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <FormSelect
                label="Counselor"
                name="counselorId"
                value={formData.counselorId}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  counselorId: e.target.value
                }))}
                options={counselors
                  .filter(counselor => {
                    // All counselors can do these services, or filter if specific
                    return true;
                  })
                  .map(counselor => ({
                    value: counselor._id || counselor.id,
                    label: counselor.fullName || counselor.username
                  }))}
                disabled={!formData.serviceType}
                required
              />

              <FormInput
                type="date"
                label="Appointment Date"
                name="date"
                value={formData.date}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  setFormData((prev) => ({ ...prev, date: selectedDate, timeSlot: '' }));
                }}
                min={new Date().toISOString().split('T')[0]}
                disabled={loading}
                required
              />

              <FormSelect
                label="Time Slot"
                name="timeSlot"
                value={formData.timeSlot}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    timeSlot: e.target.value
                  }))
                }
                options={availableSlots.map((slot) => ({
                  value: slot,
                  label: slot,
                  disabled: isTimeSlotBlocked(formData.date, slot)
                }))}
                disabled={!formData.date}
                required
              />
            </div>
          </div>

          {/* Urgent Case Fields */}
          {isUrgent && (
            <div className="space-y-4 animate-scale-in origin-top">
              <FormTextArea
                label="Reason for Urgency"
                value={formData.urgencyReason}
                onChange={(e) => setFormData(prev => ({ ...prev, urgencyReason: e.target.value }))}
                required={isUrgent}
              />
              <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 rounded-r-lg">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                      Urgent cases may require rescheduling of existing appointments.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <FormTextArea
            label="Additional Notes"
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Any other details we should know..."
          />

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!formData.date || !formData.timeSlot) ||
                (user.role === 'receptionist' && !selectedClient)}
              className={`px-6 py-2 rounded-xl text-white font-medium flex items-center shadow-lg transition-all transform active:scale-95 ${isUrgent
                ? 'bg-gradient-to-r from-red-600 to-red-500 hover:shadow-red-500/25 disabled:from-red-300 disabled:to-red-200'
                : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:shadow-blue-500/25 disabled:from-blue-300 disabled:to-blue-200'
                }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  <span>Processing...</span>
                </>
              ) : (
                <span>{isUrgent ? 'Book Urgent Appointment' : 'Confirm Booking'}</span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Client Search Modal */}
      {showClientSearch && (
        <ClientSearch
          onSelect={handleClientSelect}
          onClose={() => setShowClientSearch(false)}
        />
      )}
    </div>
  );
};

export default BookAppointment;